import { ColorField } from "@/components/cms/theme/ColorField";
import { usePuckTheme } from "@/context/cms-puck/PuckThemeContext";
import {
  DEFAULT_CATALOGUE_FILTERS_PARTS,
  normalizeCatalogueFiltersParts,
  type CatalogueFiltersParts,
  type FilterPartColors,
} from "@/lib/cms-puck/blocks/catalogue-filters";
import { type CmsColor } from "@/lib/cms/cms-theme.constants";
import { cmsNew, useCmsNew } from "@/lib/cms/cms-new-i18n";

const SECTION_CLASS =
  "space-y-3 rounded-xl border border-slate-200/70 bg-white p-3 shadow-sm";

function PartSection({
  title,
  hint,
  value,
  colors,
  onChange,
}: {
  title: string;
  hint: string;
  value: FilterPartColors;
  colors: CmsColor[];
  onChange: (next: FilterPartColors) => void;
}) {
  const cmsNewFn = useCmsNew();
  return (
    <div className={SECTION_CLASS}>
      <div>
        <p className="text-sm font-medium text-slate-800">{title}</p>
        <p className="mt-0.5 text-[11px] leading-snug text-slate-500">{hint}</p>
      </div>
      <div className="space-y-3">
        <ColorField
          label={cmsNewFn("cms.new.auto.label.fond", "Fond")}
          value={value.background}
          onChange={(background) => onChange({ ...value, background })}
          colors={colors}
        />
        <ColorField
          label={cmsNewFn("cms.new.auto.label.texte", "Texte")}
          value={value.text}
          onChange={(text) => onChange({ ...value, text })}
          colors={colors}
        />
        <ColorField
          label={cmsNewFn("cms.new.auto.label.bordure", "Bordure")}
          value={value.border}
          onChange={(border) => onChange({ ...value, border })}
          colors={colors}
        />
      </div>
    </div>
  );
}

/**
 * Champ Contenu : couleurs des 3 zones de la barre de filtres
 * (selects / recherche / bouton « Plus de filtres »).
 */
export function CatalogueFiltersPartsField({
  value,
  onChange,
}: {
  value: CatalogueFiltersParts | undefined;
  onChange: (value: CatalogueFiltersParts) => void;
}) {
  const cmsNewFn = useCmsNew();
  const theme = usePuckTheme();
  const colors: CmsColor[] = theme?.colors ?? [];
  const parts = normalizeCatalogueFiltersParts(
    value ?? DEFAULT_CATALOGUE_FILTERS_PARTS,
  );

  const setPart = (
    key: keyof CatalogueFiltersParts,
    next: FilterPartColors,
  ) => onChange({ ...parts, [key]: next });

  return (
    <div className="space-y-3">
      <p className="text-xs leading-relaxed text-slate-500">
        {cmsNewFn(
          "cms.new.hint.catalogueFilters.parts",
          "Personnalisez chaque zone de la barre. Le fond global du bloc reste dans l'onglet Design.",
        )}
      </p>
      <PartSection
        title={cmsNew("cms.new.filters.parts.selects", "Filtres (selects)")}
        hint={cmsNew(
          "cms.new.filters.parts.selectsHint",
          "Date, type, thèmes, mots-clés",
        )}
        value={parts.selects}
        colors={colors}
        onChange={(next) => setPart("selects", next)}
      />
      <PartSection
        title={cmsNew("cms.new.filters.parts.search", "Barre de recherche")}
        hint={cmsNew(
          "cms.new.filters.parts.searchHint",
          "Champ « Rechercher » et son contour",
        )}
        value={parts.search}
        colors={colors}
        onChange={(next) => setPart("search", next)}
      />
      <PartSection
        title={cmsNew(
          "cms.new.filters.parts.moreButton",
          "Bouton Plus de filtres",
        )}
        hint={cmsNew(
          "cms.new.filters.parts.moreButtonHint",
          "Bouton d'ouverture des filtres avancés",
        )}
        value={parts.moreButton}
        colors={colors}
        onChange={(next) => setPart("moreButton", next)}
      />
    </div>
  );
}
